import { Badge } from '@windmill/react-ui';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import LabeledTextArea from 'src/components/_common/LabeledTextArea/LabeledTextArea';
import Comment, { CommentType } from './Comment';
import { FormAnswerComment } from '../../FormAnswers';

interface FormQuestionDisagreementsProps {
  showNewDisagreement?: boolean;
  disagreementComment?: string;
  disagreementCommentDate?: string;
  changeDisagreementComment: (comment: string) => void;
  disagreementCommentMaxLength: number;
  teacherComment?: FormAnswerComment;
  supervisorComment?: FormAnswerComment;
  studentComment?: FormAnswerComment;
  teacherMidTermComment?: FormAnswerComment;
  supervisorMidTermComment?: FormAnswerComment;
  studentMidTermComment?: FormAnswerComment;
  activeColor?: string;
  disagreementBadgeText: string;
  noTypePicker?: boolean;
  isMidSemester: boolean;
  onBlur: () => void;
  othersComments: Array<FormAnswerComment | undefined> | undefined;
  othersMidTermComments: Array<FormAnswerComment | undefined> | undefined;
  canComment: boolean;
}

const FormQuestionDisagreements = ({
  showNewDisagreement,
  teacherComment,
  supervisorComment,
  studentComment,
  teacherMidTermComment,
  supervisorMidTermComment,
  studentMidTermComment,
  activeColor,
  disagreementComment,
  disagreementCommentDate,
  changeDisagreementComment,
  disagreementCommentMaxLength,
  disagreementBadgeText,
  noTypePicker,
  isMidSemester,
  onBlur,
  othersComments,
  othersMidTermComments,
  canComment
}: FormQuestionDisagreementsProps) => {
  const { formatMessage } = useIntl();

  function showPreviousCommentsForMidTermAnswers() {
    if (isMidSemester) return false;
    return (
      teacherMidTermComment || supervisorMidTermComment || studentMidTermComment
    );
  }

  return (
    <>
      {(showNewDisagreement ||
        noTypePicker ||
        teacherComment ||
        supervisorComment ||
        studentComment) && (
        <div className={classNames('flex flex-col', { 'mt-8': !noTypePicker })}>
          {(showNewDisagreement || noTypePicker) && (
            <div className="flex py-4  border-lightGray items-center">
              <Badge
                className={classNames(
                  `bg-${activeColor}`,
                  `text-${activeColor}Text`,
                  'text-xs font-bold mr-2 h-full',
                )}
              >
                {disagreementBadgeText}
              </Badge>
              <div className="flex flex-col w-full">
                <LabeledTextArea
                  onValueChangeHandler={changeDisagreementComment}
                  value={disagreementComment || ''}
                  className="w-full"
                  labelText={formatMessage({ id: 'comment' })}
                  onBlur={onBlur}
                  maxLength={disagreementCommentMaxLength}
                  disabled={!canComment}
                />
                <div className="text-darkergray text-sm">
                  {`${
                    disagreementComment?.length || 0
                  }/${disagreementCommentMaxLength}`}
                </div>
                <div className="text-darkergray text-sm">
                  {`${disagreementCommentDate ? `${formatMessage({ id: 'lastChanged'})}: ${disagreementCommentDate}` : ''}`}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <div>
        {teacherComment?.comment && (
          <Comment
            type={CommentType.Teacher}
            userId={teacherComment.userId!}
            commentDate={teacherComment.commentedAt!}
          >
            {teacherComment.comment}
          </Comment>
        )}
        {supervisorComment?.comment && (
          <Comment
            type={CommentType.Supervisor}
            userId={supervisorComment.userId!}
            commentDate={supervisorComment.commentedAt!}
          >
            {supervisorComment.comment}
          </Comment>
        )}
        {studentComment?.comment && (
          <Comment
            type={CommentType.Student}
            userId={studentComment.userId!}
            commentDate={studentComment.commentedAt!}
          >
            {studentComment.comment}
          </Comment>
        )}
        {othersComments &&
          othersComments.map(comment =>
            (comment?.comment ? (
              <Comment
                type={CommentType.Supervisor}
                userId={comment.userId!}
                commentDate={comment.commentedAt!}
              >
                {comment.comment}
              </Comment>
            ) : undefined),
          )}
        {showPreviousCommentsForMidTermAnswers() && (
          <>
            <h5>
              <FormattedMessage id="commentsFromMidTerm" />
            </h5>
            {teacherMidTermComment?.comment && (
              <Comment
                type={CommentType.Teacher}
                userId={teacherMidTermComment.userId!}
                commentDate={teacherMidTermComment.commentedAt!}
              >
                {teacherMidTermComment.comment}
              </Comment>
            )}
            {supervisorMidTermComment?.comment && (
              <Comment
                type={CommentType.Supervisor}
                userId={supervisorMidTermComment.userId!}
                commentDate={supervisorMidTermComment.commentedAt!}
              >
                {supervisorMidTermComment.comment}
              </Comment>
            )}
            {studentMidTermComment?.comment && (
              <Comment
                type={CommentType.Student}
                userId={studentMidTermComment.userId!}
                commentDate={studentMidTermComment.commentedAt!}
              >
                {studentMidTermComment.comment}
              </Comment>
            )}
            {othersMidTermComments &&
              othersMidTermComments.map(comment =>
                (comment?.comment ? (
                  <Comment
                    type={CommentType.Supervisor}
                    userId={comment.userId!}
                    commentDate={comment.commentedAt!}
                  >
                    {comment.comment}
                  </Comment>
                ) : undefined),
              )}
          </>
        )}
      </div>
    </>
  );
};

export default FormQuestionDisagreements;
